import { useState, useEffect, useCallback, useRef } from "react";
import { authService } from "services/Ldb/authService";
import config from "config/config";

interface UseGetSpaceUnreadCountReturn {
    unreadCount: number;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

const useGetSpaceUnreadCount = (spaceId: string, skip = false): UseGetSpaceUnreadCountReturn => {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounced fetch function to prevent excessive API calls
    const debouncedFetchUnreadCount = useCallback((delay: number = 0) => {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(async () => {
            if (!spaceId || skip) return;

            // TODO: Temporarily commented out - will re-enable later
            /*
            try {
                setIsLoading(true);
                setError(null);

                const response = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/unread-count`, {
                    method: 'GET'
                });

                if (response?.success && typeof response?.data?.count === 'number') {
                    setUnreadCount(response.data.count);
                } else {
                    // Fallback to 0 if response is not in expected format
                    setUnreadCount(0);
                }
            } catch (err) {
                console.error('Error fetching unread count:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch unread count');
                setUnreadCount(0); // Fallback to 0 on error
            } finally {
                setIsLoading(false);
            }
            */
            
            // Temporary: Just set to 0 to avoid API calls
            setIsLoading(false);
            setError(null);
            setUnreadCount(0);
        }, delay);
    }, [spaceId, skip]);

    // Immediate fetch function for initial load
    const fetchUnreadCount = useCallback(async () => {
        debouncedFetchUnreadCount(0);
    }, [debouncedFetchUnreadCount]);

    // Listen for auto-read events and update count locally
    useEffect(() => {
        const handleAutoRead = (event: CustomEvent) => {
            const { spaceId: eventSpaceId, messageId, messageIds } = event.detail || {};
            
            // Only update if this event is for our space
            if (eventSpaceId === spaceId) {
                // Calculate how many messages were marked as read
                let readCount = 0;
                if (messageIds && Array.isArray(messageIds)) {
                    readCount = messageIds.length; // Batch read
                } else if (messageId) {
                    readCount = 1; // Single message read
                }
                
                if (readCount > 0) {
                    setUnreadCount(prevCount => Math.max(0, prevCount - readCount));
                    
                    // Verify with server after a longer delay to reduce API calls (debounced)
                    debouncedFetchUnreadCount(15000); // 15 seconds after local update (reduced frequency)
                }
            }
        };

        const handleMarkAsUnread = (event: CustomEvent) => {
            const { spaceId: eventSpaceId, messageId } = event.detail || {};
            
            // Only update if this event is for our space
            if (eventSpaceId === spaceId && messageId) {
                setUnreadCount(prevCount => prevCount + 1);
                
                // Verify with server after a longer delay to reduce API calls (debounced)
                debouncedFetchUnreadCount(15000); // 15 seconds after local update (reduced frequency)
            }
        };

        window.addEventListener('space-message-marked-read', handleAutoRead as EventListener);
        window.addEventListener('space-message-marked-unread', handleMarkAsUnread as EventListener);
        
        return () => {
            window.removeEventListener('space-message-marked-read', handleAutoRead as EventListener);
            window.removeEventListener('space-message-marked-unread', handleMarkAsUnread as EventListener);
        };
    }, [spaceId]);

    // Initial fetch
    useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    // Periodic sync with server to prevent drift (every 2 minutes - reduced frequency)
    useEffect(() => {
        if (!spaceId || skip) return;

        const syncInterval = setInterval(() => {
            debouncedFetchUnreadCount(1000); // Use debounced version with 1 second delay
        }, 120000); // 2 minutes (reduced from 30 seconds to minimize API calls)

        return () => clearInterval(syncInterval);
    }, [spaceId, skip, debouncedFetchUnreadCount]);

    // Sync when user returns to the tab (visibility change)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && !skip) {
                debouncedFetchUnreadCount(2000); // Use debounced version with 2 second delay
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [debouncedFetchUnreadCount, skip]);

    // Listen for manual sync requests (e.g., after API errors)
    useEffect(() => {
        const handleSyncRequest = (event: CustomEvent) => {
            const { spaceId: eventSpaceId } = event.detail || {};
            if (eventSpaceId === spaceId) {
                debouncedFetchUnreadCount(500); // Use debounced version with 0.5 second delay
            }
        };

        window.addEventListener('space-sync-unread-count', handleSyncRequest as EventListener);
        return () => window.removeEventListener('space-sync-unread-count', handleSyncRequest as EventListener);
    }, [spaceId, debouncedFetchUnreadCount]);

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return {
        unreadCount,
        isLoading,
        error,
        refetch: fetchUnreadCount
    };
};

export default useGetSpaceUnreadCount;
